import {
  Field,
  FieldQueryable,
  FieldTypes,
  TranslationListItem,
} from '../../../models';
import { ComparisonOperator, ComparisonOperators } from '../models/criteria';

const queryableToOperators: Record<keyof FieldQueryable, ComparisonOperator> = {
  equals: ComparisonOperators.Equals,
  notEquals: ComparisonOperators.NotEqual,
  contains: ComparisonOperators.In,
  notContains: ComparisonOperators.NotIn,
  exists: ComparisonOperators.Exists,
  notExists: ComparisonOperators.NotExists,
  inRange: ComparisonOperators.Range,
  notInRange: ComparisonOperators.NotRange,
  greatThan: ComparisonOperators.GreaterThan,
  greatThanOrEqual: ComparisonOperators.GreaterThanOrEqual,
  lessThan: ComparisonOperators.LessThan,
  lessThanOrEqual: ComparisonOperators.LessThanOrEqual,
  like: ComparisonOperators.Like,
  notLike: ComparisonOperators.NotLike,
};

const operatorToQueryable: Record<string, keyof FieldQueryable> =
  Object.fromEntries(
    Object.entries(queryableToOperators).map(([key, value]) => [value, key]),
  ) as Record<string, keyof FieldQueryable>;

export enum DatePeriods {
  Days = 'D',
  Weeks = 'W',
  Months = 'M',
}

export type DatePeriod = `${DatePeriods}`;

export enum TextOperators {
  Contains = 'contains',
  StartsWith = 'startsWith',
  EndsWith = 'endsWith',
  NotContain = 'notContain',
  NotStartWith = 'notStartWith',
  NotEndWith = 'notEndWith',
}

export type TextOperator = `${TextOperators}`;

export enum DateOperators {
  InTheLast = 'inTheLast',
  InTheNext = 'inTheNext',
}

export type DateOperator = `${DateOperators}`;

export enum EmptyOperators {
  Empty = 'empty',
  NotEmpty = 'notEmpty',
}

export type EmptyOperator = `${EmptyOperators}`;

export type SubOperator = TextOperator | DateOperator | EmptyOperator;

export enum OperatorOptionId {
  Equals = ComparisonOperators.Equals,
  NotEqual = ComparisonOperators.NotEqual,
  GreaterThanOrEqual = ComparisonOperators.GreaterThanOrEqual,
  LessThanOrEqual = ComparisonOperators.LessThanOrEqual,
  GreaterThan = ComparisonOperators.GreaterThan,
  LessThan = ComparisonOperators.LessThan,
  In = ComparisonOperators.In,
  NotIn = ComparisonOperators.NotIn,
  Range = ComparisonOperators.Range,
  Contains = `${ComparisonOperators.Like}:${TextOperators.Contains}`,
  NotContain = `${ComparisonOperators.NotLike}:${TextOperators.NotContain}`,
  StartsWith = `${ComparisonOperators.Like}:${TextOperators.StartsWith}`,
  EndsWith = `${ComparisonOperators.Like}:${TextOperators.EndsWith}`,
  NotStartWith = `${ComparisonOperators.NotLike}:${TextOperators.NotStartWith}`,
  NotEndWith = `${ComparisonOperators.NotLike}:${TextOperators.NotEndWith}`,
  IsEmpty = `${ComparisonOperators.Equals}:${EmptyOperators.Empty}`,
  IsNotEmpty = `${ComparisonOperators.NotEqual}:${EmptyOperators.NotEmpty}`,
  InTheLast = `${ComparisonOperators.Range}:${DateOperators.InTheLast}`,
  InTheNext = `${ComparisonOperators.Range}:${DateOperators.InTheNext}`,
}

export interface OperatorListItem extends TranslationListItem<string> {
  operator: ComparisonOperator;
  subOperator?: SubOperator;
}

export const OperatorOptions: Record<OperatorOptionId, OperatorListItem> = {
  [OperatorOptionId.Equals]: {
    id: OperatorOptionId.Equals,
    operator: ComparisonOperators.Equals,
    translationID: 'criteria.equals',
    resourceID: 'JSS_GenTextSearchOptMatch',
  },
  [OperatorOptionId.NotEqual]: {
    id: OperatorOptionId.NotEqual,
    operator: ComparisonOperators.NotEqual,
    translationID: 'criteria.notEquals',
    resourceID: 'JSS_GenTextSearchOptNotmatch',
  },
  [OperatorOptionId.GreaterThanOrEqual]: {
    id: OperatorOptionId.GreaterThanOrEqual,
    operator: ComparisonOperators.GreaterThanOrEqual,
    translationID: 'criteria.greatOrEqual',
    resourceID: 'JSS_GenTextSearchOptGreaterEqual',
  },
  [OperatorOptionId.LessThanOrEqual]: {
    id: OperatorOptionId.LessThanOrEqual,
    operator: ComparisonOperators.LessThanOrEqual,
    translationID: 'criteria.lessOrEqual',
    resourceID: 'JSS_GenTextSearchOptLessEqual',
  },
  [OperatorOptionId.GreaterThan]: {
    id: OperatorOptionId.GreaterThan,
    operator: ComparisonOperators.GreaterThan,
    translationID: 'criteria.greaterThan',
    resourceID: 'JSS_GenTextSearchOptGreater',
  },
  [OperatorOptionId.LessThan]: {
    id: OperatorOptionId.LessThan,
    operator: ComparisonOperators.LessThan,
    translationID: 'criteria.lessThan',
    resourceID: 'JSS_GenTextSearchOptLess',
  },
  [OperatorOptionId.In]: {
    id: OperatorOptionId.In,
    operator: ComparisonOperators.In,
    translationID: 'criteria.contains',
    resourceID: 'JSS_GenTextSearchOptContain',
  },
  [OperatorOptionId.NotIn]: {
    id: OperatorOptionId.NotIn,
    operator: ComparisonOperators.NotIn,
    translationID: 'criteria.notContain',
    resourceID: 'JSS_GenTextSearchOptNotcontain',
  },
  [OperatorOptionId.Range]: {
    id: OperatorOptionId.Range,
    operator: ComparisonOperators.Range,
    translationID: 'criteria.between',
    resourceID: 'JSS_GenTextSearchOptBetween',
  },
  [OperatorOptionId.Contains]: {
    id: OperatorOptionId.Contains,
    operator: ComparisonOperators.Like,
    subOperator: TextOperators.Contains,
    translationID: 'criteria.contains',
    resourceID: 'JSS_GenTextSearchOptContain',
  },
  [OperatorOptionId.NotContain]: {
    id: OperatorOptionId.NotContain,
    operator: ComparisonOperators.NotLike,
    subOperator: TextOperators.NotContain,
    translationID: 'criteria.notContain',
    resourceID: 'JSS_GenTextSearchOptNotcontain',
  },
  [OperatorOptionId.StartsWith]: {
    id: OperatorOptionId.StartsWith,
    operator: ComparisonOperators.Like,
    subOperator: TextOperators.StartsWith,
    translationID: 'criteria.startsWith',
    resourceID: 'JSS_GenTextSearchOptStart',
  },
  [OperatorOptionId.NotStartWith]: {
    id: OperatorOptionId.NotStartWith,
    operator: ComparisonOperators.NotLike,
    subOperator: TextOperators.NotStartWith,
    translationID: 'criteria.notStartWith',
    resourceID: 'JSS_GenTextSearchOptNotstart',
  },
  [OperatorOptionId.EndsWith]: {
    id: OperatorOptionId.EndsWith,
    operator: ComparisonOperators.Like,
    subOperator: TextOperators.EndsWith,
    translationID: 'criteria.endsWith',
    resourceID: 'JSS_GenTextSearchOptEnd',
  },
  [OperatorOptionId.NotEndWith]: {
    id: OperatorOptionId.NotEndWith,
    operator: ComparisonOperators.NotLike,
    subOperator: TextOperators.NotEndWith,
    translationID: 'criteria.notEndWith',
    resourceID: 'JSS_GenTextSearchOptNotend',
  },
  [OperatorOptionId.IsEmpty]: {
    id: OperatorOptionId.IsEmpty,
    operator: ComparisonOperators.Equals,
    subOperator: EmptyOperators.Empty,
    translationID: 'criteria.isEmpty',
    resourceID: 'JSS_GenTextSearchOptIsempty',
  },
  [OperatorOptionId.IsNotEmpty]: {
    id: OperatorOptionId.IsNotEmpty,
    operator: ComparisonOperators.NotEqual,
    subOperator: EmptyOperators.Empty,
    translationID: 'criteria.isNotEmpty',
    resourceID: 'JSS_GenTextSearchOptNotisempty',
  },
  [OperatorOptionId.InTheLast]: {
    id: OperatorOptionId.InTheLast,
    operator: ComparisonOperators.Range,
    subOperator: DateOperators.InTheLast,
    translationID: 'criteria.inTheLast',
    resourceID: 'JSS_GenTextSearchOptInTheLast',
  },
  [OperatorOptionId.InTheNext]: {
    id: OperatorOptionId.InTheNext,
    operator: ComparisonOperators.Range,
    subOperator: DateOperators.InTheNext,
    translationID: 'criteria.inTheNext',
    resourceID: 'JSS_GenTextSearchOptInTheNext',
  },
};

export function getOperatorOptions(
  ids: OperatorOptionId[],
): OperatorListItem[] {
  const options: OperatorListItem[] = [];
  ids.forEach((id) => options.push(OperatorOptions[id]));
  return options;
}

export function getOperatorsForField(field: Field): OperatorListItem[] {
  let operators: OperatorListItem[];

  switch (field.type) {
    case FieldTypes.String:
      operators = getOperatorOptions([
        OperatorOptionId.Equals,
        OperatorOptionId.NotEqual,
        OperatorOptionId.Contains,
        OperatorOptionId.StartsWith,
        OperatorOptionId.EndsWith,
        OperatorOptionId.NotContain,
        OperatorOptionId.NotStartWith,
        OperatorOptionId.NotEndWith,
        OperatorOptionId.IsEmpty,
        OperatorOptionId.IsNotEmpty,
      ]);
      break;
    case FieldTypes.Number:
      operators = getOperatorOptions([
        OperatorOptionId.Equals,
        OperatorOptionId.NotEqual,
        OperatorOptionId.GreaterThan,
        OperatorOptionId.GreaterThanOrEqual,
        OperatorOptionId.LessThan,
        OperatorOptionId.LessThanOrEqual,
        OperatorOptionId.Range,
        OperatorOptionId.IsEmpty,
        OperatorOptionId.IsNotEmpty,
      ]);
      break;
    case FieldTypes.DateTime:
      operators = getOperatorOptions([
        OperatorOptionId.Equals,
        OperatorOptionId.InTheLast,
        OperatorOptionId.InTheNext,
        OperatorOptionId.Range,
        OperatorOptionId.IsEmpty,
        OperatorOptionId.IsNotEmpty,
      ]);
      break;
    case FieldTypes.Selector:
      operators = getOperatorOptions([
        OperatorOptionId.In,
        OperatorOptionId.NotIn,
        OperatorOptionId.IsEmpty,
        OperatorOptionId.IsNotEmpty,
      ]);
      break;
    default:
      operators = getOperatorOptions([
        OperatorOptionId.Equals,
        OperatorOptionId.NotEqual,
      ]);
      break;
  }

  if (
    !field.metadata?.queryable ||
    !Object.values(field.metadata.queryable).some((operation) => operation)
  ) {
    return operators;
  }

  return operators.filter((item) => {
    if (
      item.subOperator === EmptyOperators.Empty ||
      item.subOperator === EmptyOperators.NotEmpty
    ) {
      return field.type === 'string' || field.metadata?.nullable;
    }
    const key = operatorToQueryable[item.operator];
    return key ? field.metadata?.queryable?.[key] : false;
  });
}
